import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Calendar, UserPlus, Users, LogIn } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { useClubPaths } from '@/hooks/useClubPaths';
import { useEmbedHost } from '@/contexts/EmbedHostContext';
import { buildClubAuthLinkOutUrl } from '@/lib/clubPaths';
import { useSubscription } from '@/hooks/useSubscription';
import type { PublicClubProfile } from '@/services/publicClub';
import { Button } from '@/components/ui';
import { BrowserImage } from '@/components/ui/BrowserImage';
import { PublicChrome } from '@/components/public/PublicChrome';
import { cn } from '@/lib/utils';

interface PublicClubLayoutProps {
  profile: PublicClubProfile;
  children: React.ReactNode;
}

export function PublicClubLayout({ profile, children }: PublicClubLayoutProps) {
  const { user } = useAuthStore();
  const { p, fields } = useClubPaths(profile.club);
  const { isEmbedMode } = useEmbedHost();
  const { currentTier } = useSubscription();
  const location = useLocation();
  const { club, courtHireAvailable, membershipJoinAvailable } = profile;

  if (!p || !fields) return <>{children}</>;

  const navItems = [
    { to: p(''), label: 'Home', icon: Home, show: true },
    { to: p('/court-hire'), label: 'Court hire', icon: Calendar, show: courtHireAvailable },
    { to: p('/join'), label: 'Join', icon: UserPlus, show: membershipJoinAvailable },
    { to: p('/membership'), label: 'Membership', icon: Users, show: true },
  ].filter((item) => item.show);

  const membershipReturn = `${location.pathname}${location.search}`;
  const loginHref = isEmbedMode
    ? buildClubAuthLinkOutUrl(
        { ...fields, orgTier: currentTier as typeof fields.orgTier },
        '/login',
        membershipReturn
      )
    : p(`/login?returnUrl=${encodeURIComponent(membershipReturn)}`);

  const layoutBody = (
    <div className="flex min-h-screen flex-col bg-background">
      <nav className="border-b border-border bg-card shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <Link to={p('')} className="flex min-w-0 items-center gap-3">
              {club.logoUrl ? (
                <BrowserImage
                  src={club.logoUrl}
                  alt={club.name}
                  className="h-10 w-auto max-w-[140px] object-contain"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="truncate text-lg font-bold gradient-text">{club.name}</span>
              )}
            </Link>

            <div className="hidden items-center gap-1 md:flex">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      active
                        ? 'bg-primary/10 text-primary-700 dark:text-primary-300'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {user ? (
                isEmbedMode ? (
                  <Button variant="outline" size="sm" asChild>
                    <a href={loginHref} target="_blank" rel="noopener noreferrer">
                      <LogIn className="h-4 w-4" aria-hidden />
                      Member portal
                    </a>
                  </Button>
                ) : (
                  <Button variant="default" size="sm" asChild>
                    <Link to={p('/membership')}>
                      <LogIn className="h-4 w-4" aria-hidden />
                      Member portal
                    </Link>
                  </Button>
                )
              ) : isEmbedMode ? (
                <Button variant="default" size="sm" asChild>
                  <a href={loginHref} target="_blank" rel="noopener noreferrer">
                    <LogIn className="h-4 w-4" aria-hidden />
                    Sign in
                  </a>
                </Button>
              ) : (
                <Button variant="default" size="sm" asChild>
                  <Link to={p(`/login?returnUrl=${encodeURIComponent(membershipReturn)}`)}>
                    <LogIn className="h-4 w-4" aria-hidden />
                    Sign in
                  </Link>
                </Button>
              )}
            </div>
          </div>

          <div className="flex gap-1 overflow-x-auto border-t border-border py-2 md:hidden">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
                    active ? 'bg-primary/10 text-primary-700' : 'text-muted-foreground'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      <main className="flex-1">{children}</main>
    </div>
  );

  return <PublicChrome chrome={layoutBody}>{children}</PublicChrome>;
}
